import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { DealAdvisorCard } from '@/components/DealAdvisorCard';
import { DealGallery } from '@/components/DealGallery';
import { DealMap } from '@/components/DealMap';
import { StatusPill } from '@/components/StatusPill';
import { BenchmarkPanel } from '@/components/BenchmarkPanel';
import { ExpandableDescription } from '@/components/ExpandableDescription/ExpandableDescription';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { Skeleton, SkeletonText } from '@/components/Skeleton';
import { AuthRequiredDialog, type AuthRequiredAction } from '@/components/AuthRequiredDialog';
import { AccountNudgeDialog } from '@/components/AccountNudgeDialog';
import {
  ExpressInterestModal,
  type InterestModalSubmitValues,
} from '@/components/ExpressInterestModal';
import {
  RequestCallbackModal,
  type CallbackModalSubmitValues,
} from '@/components/RequestCallbackModal';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { createBuyerRequest, SavedDealCapError } from '@/lib/db/buyer';
import { postBuyerRequest, type BuyerRequestErrorCode } from '@/lib/buyer-request-api';
import { useRecaptchaToken } from '@/lib/useRecaptchaToken';
import { env } from '@/lib/env';
import { formatAed, formatDiscountPct, formatNumber, formatPublishedDate } from '@/lib/format';
import { discountDisplayMode } from '@/lib/discount-display';
import { buildDealSeoMeta } from '@/lib/seo/deal-meta';
import { useSeoHead } from '@/lib/seo/useSeoHead';
import { getWhatsAppHref, pickAdvisorWhatsAppPhone } from '@/lib/whatsapp';

import { recordDealView } from '@/lib/db/buyer/views-history';
import { listAdvisors, type Advisor } from '@/lib/db/admin/advisors';
import { mapDealRowToDealView } from './DealsPage.data';
import { useDealBySlug, useDealSave } from './DealPage.data';

type ConversionMode = 'interest' | 'callback';

function DealPageSkeleton() {
  // Structured placeholder while `useDealBySlug` is in flight: header + gallery
  // + body lines, reserving ~70vh so layout shift on success stays bounded.
  return (
    <div
      className="public"
      style={{ minHeight: '70vh', padding: '48px 0' }}
      aria-busy="true"
      aria-live="polite"
      data-testid="deal-page-skeleton"
    >
      <div className="public__inner">
        <Skeleton width={140} height={12} />
        <div style={{ height: 16 }} />
        <Skeleton width="58%" height={36} />
        <div style={{ height: 10 }} />
        <Skeleton width="32%" height={16} />
        <div style={{ height: 28 }} />
        <Skeleton width="100%" height={420} />
        <div style={{ height: 32 }} />
        <SkeletonText lines={6} lastWidth="45%" gap={12} />
      </div>
    </div>
  );
}

export function DealPage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useAuth();
  const isMobile = useIsMobile();
  const getRecaptchaToken = useRecaptchaToken();
  const dealQuery = useDealBySlug(slug);

  // ADR-025 §A — Save state hydrated from `saved_deals` for the current user
  // so the heart reflects DB truth even after a hard refresh.
  const userId = auth.status === 'authenticated' ? auth.user.id : null;
  const dealRowId = dealQuery.status === 'success' ? dealQuery.deal.id : null;
  const { state: saveState, toggle: toggleSave } = useDealSave(dealRowId, userId);

  const [conversionMode, setConversionMode] = useState<ConversionMode | null>(null);
  const [authDialogAction, setAuthDialogAction] = useState<AuthRequiredAction | null>(null);
  // After a SUCCESSFUL anonymous Interest/Callback (ADR-053), surface the
  // non-blocking "create an account" nudge with the submitted name + email.
  const [nudge, setNudge] = useState<{ email: string; fullName: string } | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [sidebarSticky, setSidebarSticky] = useState(true);

  // Post-login action resume (Spec V1.1 §M4/§M5; supersedes ADR-025 §D). The
  // auth redirect returns here with `?action=save|interest|callback`; we
  // capture the intent on mount, strip the param, and replay it once the user
  // is authenticated and the relevant state is ready.
  const resumeActionRef = useRef<AuthRequiredAction | null>(null);

  // Record deal view (dedup 1/user/deal/24h via DB unique index).
  useEffect(() => {
    if (dealRowId) {
      recordDealView(dealRowId, userId).catch(() => {});
    }
  }, [dealRowId, userId]);

  // Load the assigned advisor + the default advisor (WhatsApp cascade). We
  // fetch the full list once the deal resolves so the default is known even
  // when no advisor is assigned to this deal.
  const [advisor, setAdvisor] = useState<Advisor | null>(null);
  const [defaultAdvisor, setDefaultAdvisor] = useState<Advisor | null>(null);
  const dealLoaded = dealQuery.status === 'success';
  const advisorId = dealLoaded ? dealQuery.deal.advisor_id : null;
  useEffect(() => {
    if (!dealLoaded) return;
    listAdvisors()
      .then((list) => {
        setAdvisor(advisorId ? (list.find((a) => a.id === advisorId) ?? null) : null);
        setDefaultAdvisor(list.find((a) => a.is_default) ?? null);
      })
      .catch(() => {});
  }, [dealLoaded, advisorId]);

  // WhatsApp number cascade (Item 9): assigned advisor → default advisor →
  // agency number. Only active advisors with a dialable phone qualify; when
  // none do, getWhatsAppHref falls back to APP.whatsappNumber.
  const whatsAppPhone = pickAdvisorWhatsAppPhone(advisor, defaultAdvisor);

  // Advisor card (M13): the assigned advisor, else the table default — same
  // active-first cascade as WhatsApp. No advisor at all → no card (strict,
  // never a hardcoded fallback identity).
  const displayAdvisor =
    (advisor?.is_active ? advisor : null) ?? (defaultAdvisor?.is_active ? defaultAdvisor : null);

  // UB3 — détach sticky if sidebar is taller than the viewport.
  useEffect(() => {
    const check = () => {
      if (!sidebarRef.current) return;
      const sidebarHeight = sidebarRef.current.offsetHeight;
      setSidebarSticky(sidebarHeight + 96 <= window.innerHeight);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // On mount: capture a valid `?action=` intent set by the post-login redirect
  // and strip it from the URL so a refresh / back-button never replays it.
  useEffect(() => {
    const a = searchParams.get('action');
    if (a === 'save' || a === 'interest' || a === 'callback') {
      resumeActionRef.current = a;
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('action');
      setSearchParams(nextParams, { replace: true });
    }
    // Mount-only: the action arrives once, on the post-login navigation here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resume Express interest / Request callback once the deal is loaded for an
  // authenticated user — reopens the relevant modal.
  useEffect(() => {
    const a = resumeActionRef.current;
    if (!a || a === 'save') return;
    if (auth.status !== 'authenticated') return;
    if (dealQuery.status !== 'success') return;
    resumeActionRef.current = null;
    setConversionMode(a);
  }, [auth.status, dealQuery.status]);

  // Resume Save once the saved-state has hydrated; add the favourite if it is
  // not already saved (honours the original "save" intent).
  useEffect(() => {
    if (resumeActionRef.current !== 'save') return;
    if (auth.status !== 'authenticated') return;
    if (saveState.status !== 'idle') return;
    resumeActionRef.current = null;
    if (saveState.saved) return;
    void toggleSave()
      .then((nextSaved) => {
        if (nextSaved) toast.success(t('deal.savedToast'));
      })
      .catch((err) => {
        if (err instanceof SavedDealCapError) toast.error(t('buyer.saved.errors.capReached'));
        else toast.error(t('buyer.errors.generic'));
      });
  }, [auth.status, saveState.status, saveState.saved, toggleSave, t]);

  // Mapped view-model — only computed when the deal row is available.
  // Mirrors the catalogue / DealCard shape so existing DealCard / BenchmarkPanel
  // consume the same `DealView` interface without per-call adaption.
  const deal = useMemo(
    () => (dealQuery.status === 'success' ? mapDealRowToDealView(dealQuery.deal) : null),
    [dealQuery],
  );

  // `null` while loading keeps the previous head instead of flashing a
  // half-built one; the real meta lands with the deal row.
  const seoMeta = useMemo(() => (deal ? buildDealSeoMeta(deal) : null), [deal]);
  useSeoHead(seoMeta);

  if (dealQuery.status === 'loading' || dealQuery.status === 'error') {
    // Treat error as loading-fallback rather than crashing the page; the
    // network error is logged in dev console by Supabase JS, and the
    // skeleton stays on screen until React Router takes the user away.
    return <DealPageSkeleton />;
  }

  if (dealQuery.status === 'notFound') {
    return <Navigate to="/404" replace />;
  }

  if (!deal) {
    // Unreachable — kept as a type-narrowing safety net for the compiler.
    return <DealPageSkeleton />;
  }

  // ADR-047 — scope-aware discount display mode, shared by the gallery hero
  // overlay and the sidebar tag so a community context benchmark is never shown
  // as a strong same-building discount.
  const discountMode = discountDisplayMode(deal.dldConfidence, deal.dldScope, deal.discountPct);

  const whatsAppMessage = t('dealCard.whatsappMessage', {
    buildingTitle: deal.buildingTitle,
    referenceId: deal.referenceId,
    dealUrl: typeof window !== 'undefined' ? window.location.href : '',
  });

  const specs: Array<{ label: string; value: string | number }> = [
    {
      label: t('deal.specs.type'),
      value: deal.status === 'READY' ? t('deal.specs.ready') : t('deal.specs.offPlan'),
    },
    {
      label: t('deal.specs.propertyType'),
      value: deal.propertyType ? t(`catalogue.filters.propertyTypeOpts.${deal.propertyType}`) : '—',
    },
    { label: t('deal.specs.area'), value: `${formatNumber(deal.areaSqft)} sqft` },
    { label: t('deal.specs.bedrooms'), value: deal.bedrooms },
    { label: t('deal.specs.bathrooms'), value: deal.bathrooms },
    { label: t('deal.specs.floor'), value: deal.floor ?? '—' },
    { label: t('deal.specs.view'), value: deal.view ?? '—' },
    { label: t('deal.specs.handover'), value: deal.handover ?? '—' },
    {
      label: t('deal.specs.status'),
      value: t(`dealStatus.${deal.status === 'READY' ? 'ready' : 'offPlan'}`),
    },
  ];

  const handleSave = async () => {
    if (auth.status !== 'authenticated') {
      setAuthDialogAction('save');
      return;
    }
    try {
      const nextSaved = await toggleSave();
      toast.success(nextSaved ? t('deal.savedToast') : t('deal.removedToast'));
    } catch (err) {
      if (err instanceof SavedDealCapError) {
        toast.error(t('buyer.saved.errors.capReached'));
        return;
      }
      toast.error(t('buyer.errors.generic'));
    }
  };

  // ADR-053 — Interest / Callback are now open to anonymous visitors: open the
  // modal directly for everyone. Save still requires auth (handleSave gates it
  // via AuthRequiredDialog). Anonymous submits go through the public Edge in
  // the handlers below; authenticated submits keep the RLS-gated path.
  const openConversion = (mode: ConversionMode) => {
    setConversionMode(mode);
  };

  const handleSignInRedirect = () => {
    const action = authDialogAction;
    setAuthDialogAction(null);
    // Build the post-login return URL = current deal URL with the intended
    // action embedded, passed as `redirect` (the param the login side + Spec
    // V1.1 §M4/§M5 use). SignInPage / AuthCallbackPage bring the user back here
    // and the resume effects above replay the action.
    const returnParams = new URLSearchParams(location.search);
    if (action) returnParams.set('action', action);
    const returnSearch = returnParams.toString();
    const returnTo = `${location.pathname}${returnSearch ? `?${returnSearch}` : ''}`;
    navigate(`/signin?redirect=${encodeURIComponent(returnTo)}`);
  };

  const prefilledProfile =
    auth.status === 'authenticated'
      ? {
          fullName:
            `${auth.profile.first_name ?? ''} ${auth.profile.last_name ?? ''}`.trim() ||
            auth.user.email,
          email: auth.user.email,
          phone: auth.profile.phone ?? '',
        }
      : null;

  const buyerErrorMessage = (code: BuyerRequestErrorCode): string => {
    switch (code) {
      case 'rate_limited':
        return t('buyer.errors.rateLimited');
      case 'recaptcha_failed':
      case 'recaptcha_unreachable':
        return t('buyer.errors.recaptcha');
      default:
        return t('buyer.errors.generic');
    }
  };

  // Anonymous Interest/Callback (ADR-053): reCAPTCHA token → public Edge. On
  // success, close the modal and open the account-creation nudge with the
  // submitted name + email. Errors are thrown so the modal surfaces them inline.
  const submitAnonymousRequest = async (
    type: ConversionMode,
    values: {
      fullName: string;
      email: string;
      phone: string;
      message?: string | null;
      slot?: string;
      callbackDate?: string;
    },
  ) => {
    if (!dealRowId) throw new Error(t('buyer.errors.generic'));

    let token: string | null = null;
    try {
      token = await getRecaptchaToken(type);
    } catch {
      token = null;
    }
    // Never call the Edge with an empty token when reCAPTCHA is configured.
    if (!token && env.recaptchaSiteKey) {
      throw new Error(t('buyer.errors.recaptcha'));
    }

    const locale = (['en', 'fr', 'ar'] as const).find((l) => i18n.language.startsWith(l)) ?? 'en';
    const result = await postBuyerRequest({
      dealId: dealRowId,
      type,
      fullName: values.fullName,
      email: values.email,
      phone: values.phone,
      message: values.message ?? null,
      callbackSlot: values.slot ?? null,
      callbackDate: values.callbackDate ?? null,
      recaptchaToken: token ?? '',
      locale,
    });

    if (!result.ok) {
      throw new Error(buyerErrorMessage(result.code));
    }

    setConversionMode(null);
    setNudge({ email: values.email, fullName: values.fullName });
  };

  const handleInterestSubmit = async (values: InterestModalSubmitValues) => {
    if (auth.status === 'authenticated') {
      if (!userId || !dealRowId) throw new Error('not_authenticated');
      await createBuyerRequest({
        userId,
        dealId: dealRowId,
        type: 'interest',
        contactPhone: values.phone,
        message: values.message || null,
      });
      toast.success(t('deal.sentToast'));
      setConversionMode(null);
      return;
    }
    await submitAnonymousRequest('interest', values);
  };

  const handleCallbackSubmit = async (values: CallbackModalSubmitValues) => {
    if (auth.status === 'authenticated') {
      if (!userId || !dealRowId) throw new Error('not_authenticated');
      await createBuyerRequest({
        userId,
        dealId: dealRowId,
        type: 'callback',
        contactPhone: values.phone,
        message: values.message || null,
        callbackSlot: values.slot,
        callbackDatePreference: values.callbackDate,
      });
      toast.success(t('deal.sentToast'));
      setConversionMode(null);
      return;
    }
    await submitAnonymousRequest('callback', values);
  };

  const saved =
    saveState.status === 'idle' || saveState.status === 'toggling' ? saveState.saved : false;

  const pois = t('deal.location.pois', { returnObjects: true }) as string[];
  const galleryPhotos = (deal.photos ?? []).map((p) => ({ url: p.url, alt_text: p.alt_text }));

  return (
    <div className="public" style={{ paddingBottom: isMobile ? 56 : undefined }}>
      <nav className="deal-breadcrumb" aria-label="breadcrumb">
        <div className="deal-breadcrumb__inner">
          <Link to="/">{t('deal.breadcrumb.deals')}</Link>
          <span className="deal-breadcrumb__sep" aria-hidden="true">
            ·
          </span>
          <span>{deal.community}</span>
          {dealQuery.deal.building && (
            <>
              <span className="deal-breadcrumb__sep" aria-hidden="true">
                ·
              </span>
              <span className="deal-breadcrumb__current">{dealQuery.deal.building}</span>
            </>
          )}
        </div>
      </nav>

      <section className="deal-header">
        <div className="deal-header__inner">
          <div className="deal-header__id">{t('deal.kicker', { id: deal.referenceId })}</div>
          <h1 className="deal-header__title">{deal.buildingTitle}.</h1>
          <div className="deal-header__pills">
            <StatusPill status={deal.status} />
            {deal.isPick ? (
              <span className="status-pill status-pill--ready">{t('deal.pillPick')}</span>
            ) : null}
          </div>
        </div>
      </section>

      <section className="deal-gallery">
        <DealGallery
          photos={galleryPhotos}
          dealTitle={deal.buildingTitle}
          discountPct={deal.discountPct}
          dldConfidence={deal.dldConfidence}
          dldScope={deal.dldScope}
          propertyType={deal.propertyType}
        />
      </section>

      <section className="deal-body">
        <div>
          <div className="deal-card-section">
            <div className="deal-section__kicker">{t('deal.specs.title')}</div>
            <div className="deal-specs">
              {specs.map((s) => (
                <div key={s.label} className="deal-specs__row">
                  <span className="deal-specs__label">{s.label}</span>
                  <span className="deal-specs__value">{s.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="deal-card-section">
            <div className="deal-section__kicker">{t('deal.property.title')}</div>
            <ExpandableDescription
              text={deal.description}
              html
              maxLines={5}
              readMoreLabel={t('deal.property.readMore')}
              showLessLabel={t('deal.property.showLess')}
              className="deal-description"
            />
          </div>

          <BenchmarkPanel
            dealId={dealQuery.deal.id}
            askingPrice={Number(dealQuery.deal.asking_price)}
            community={dealQuery.deal.community}
            building={dealQuery.deal.building}
            zone={dealQuery.deal.zone}
          />

          {/* Mobile: the sidebar (and its advisor card) is not rendered, so the
           * advisor surfaces inline right after the price benchmark (PO
           * placement 2026-06-04). In-flow → no overlap with the fixed
           * bottom bar. */}
          {isMobile && displayAdvisor && (
            <div style={{ marginTop: 24 }}>
              <DealAdvisorCard advisor={displayAdvisor} />
            </div>
          )}

          <div className="deal-card-section" style={{ marginTop: 24 }}>
            <div className="deal-section__kicker">{t('deal.location.title')}</div>
            <DealMap
              latitude={deal.latitude}
              longitude={deal.longitude}
              title={deal.buildingTitle}
            />
            <div className="deal-location__pois">
              {pois.map((p) => (
                <span key={p} className="deal-location__poi">
                  · {p}
                </span>
              ))}
            </div>
            <div className="deal-location__meta">
              {t('deal.location.meta', {
                published: formatPublishedDate(deal.publishedAt, i18n.language),
              })}
            </div>
          </div>
        </div>

        {!isMobile && (
          <aside
            ref={sidebarRef}
            className={`deal-sidebar${sidebarSticky ? ' deal-sidebar--sticky' : ''}`}
          >
            <div className="deal-sidebar__price-card">
              <div className="deal-sidebar__kicker">{t('deal.sidebar.askingPrice')}</div>
              <div className="deal-sidebar__price">{formatAed(deal.askingAed)}</div>
              {discountMode !== 'suppressed' && (
                <div className="deal-sidebar__discount">
                  {discountMode === 'building' &&
                    t('deal.sidebar.discountTag', { pct: formatDiscountPct(deal.discountPct) })}
                  {discountMode === 'project_group' &&
                    t('deal.sidebar.belowProjectBenchmark', {
                      pct: formatDiscountPct(deal.discountPct),
                    })}
                  {discountMode === 'community' &&
                    t('deal.sidebar.belowCommunityBenchmark', {
                      pct: formatDiscountPct(deal.discountPct),
                    })}
                  {discountMode === 'zone' &&
                    t('deal.sidebar.belowZoneBenchmark', {
                      pct: formatDiscountPct(deal.discountPct),
                    })}
                  {discountMode === 'developer' &&
                    t('deal.sidebar.vsOriginalPrice', {
                      pct: formatDiscountPct(deal.discountPct),
                    })}
                </div>
              )}
              <button
                type="button"
                className={`deal-sidebar__btn deal-sidebar__btn--save${saved ? ' is-saved' : ''}`}
                onClick={() => {
                  void handleSave();
                }}
                aria-label={saved ? t('deal.sidebar.removeAria') : t('deal.sidebar.saveAria')}
                aria-pressed={saved}
              >
                <span aria-hidden="true">{saved ? '♥' : '♡'}</span>
                <span>{t('deal.sidebar.save')}</span>
              </button>
              <WhatsAppButton
                label={t('actions.contactWhatsapp')}
                fullWidth
                message={whatsAppMessage}
                phone={whatsAppPhone}
                trackSource="deal"
                trackDealId={deal.id}
              />
              <button
                type="button"
                className="deal-sidebar__btn deal-sidebar__btn--primary"
                onClick={() => openConversion('interest')}
              >
                {t('deal.sidebar.interest')}
              </button>
              <button
                type="button"
                className="deal-sidebar__btn"
                onClick={() => openConversion('callback')}
              >
                {t('deal.sidebar.callback')}
              </button>
            </div>

            {displayAdvisor && <DealAdvisorCard advisor={displayAdvisor} />}

            <div className="deal-sidebar__security">
              <div className="deal-sidebar__kicker">{t('deal.sidebar.securityKicker')}</div>
              {(t('deal.sidebar.securityItems', { returnObjects: true }) as string[]).map(
                (item) => (
                  <div key={item} className="deal-sidebar__security-item">
                    <span className="deal-sidebar__security-bullet" aria-hidden="true">
                      ◆
                    </span>
                    <span>{item}</span>
                  </div>
                ),
              )}
            </div>
          </aside>
        )}
      </section>

      <section className="public-cta-dark" style={{ padding: '56px 0' }}>
        <div className="public-cta-dark__inner">
          <h2
            className="public-cta-dark__title"
            style={{ marginBottom: 28, fontSize: 'clamp(1.5rem, 3vw + 0.5rem, 2.25rem)' }}
          >
            {t('deal.ctaDark')}
          </h2>
          <div className="public__cta-row public__cta-row--center">
            <WhatsAppButton
              label={t('actions.contactWhatsapp')}
              size="lg"
              message={whatsAppMessage}
              phone={whatsAppPhone}
              trackSource="deal"
              trackDealId={deal.id}
            />
            <button
              type="button"
              className="btn btn--paper btn--lg"
              onClick={() => openConversion('callback')}
            >
              {t('deal.callbackPrimary')}
            </button>
          </div>
        </div>
      </section>

      {/* UB4 — mobile bottom bar */}
      {isMobile ? (
        <div className="deal-bottom-bar">
          <a
            href={getWhatsAppHref(undefined, whatsAppPhone)}
            target="_blank"
            rel="noopener noreferrer"
            className="deal-bottom-bar__main"
          >
            {t('deal.bottomBar.chat')}
          </a>
          <button
            type="button"
            onClick={() => {
              void handleSave();
            }}
            aria-label={saved ? t('deal.bottomBar.removeAria') : t('deal.bottomBar.saveAria')}
            className={`deal-bottom-bar__icon-btn deal-bottom-bar__icon-btn--save${saved ? ' is-saved' : ''}`}
          >
            {saved ? '♥' : '♡'}
          </button>
          <button
            type="button"
            onClick={() => openConversion('interest')}
            aria-label={t('deal.bottomBar.interestAria')}
            className="deal-bottom-bar__icon-btn"
          >
            →
          </button>
        </div>
      ) : null}

      <ExpressInterestModal
        open={conversionMode === 'interest'}
        dealId={deal.id}
        dealTitle={deal.buildingTitle}
        referenceId={deal.referenceId}
        priceLabel={formatAed(deal.askingAed)}
        prefilledProfile={prefilledProfile}
        onClose={() => setConversionMode(null)}
        onSubmit={handleInterestSubmit}
      />

      <RequestCallbackModal
        open={conversionMode === 'callback'}
        dealId={deal.id}
        dealTitle={deal.buildingTitle}
        referenceId={deal.referenceId}
        priceLabel={formatAed(deal.askingAed)}
        prefilledProfile={prefilledProfile}
        onClose={() => setConversionMode(null)}
        onSubmit={handleCallbackSubmit}
      />

      <AuthRequiredDialog
        open={authDialogAction !== null}
        title={authDialogAction ? t(`deal.authRequired.${authDialogAction}.title`) : ''}
        body={authDialogAction ? t(`deal.authRequired.${authDialogAction}.body`) : ''}
        signInLabel={t('deal.authRequired.signIn')}
        cancelLabel={t('deal.authRequired.cancel')}
        onCancel={() => setAuthDialogAction(null)}
        onSignIn={handleSignInRedirect}
      />

      <AccountNudgeDialog
        open={nudge !== null}
        email={nudge?.email ?? ''}
        fullName={nudge?.fullName ?? ''}
        onClose={() => setNudge(null)}
      />
    </div>
  );
}
